import React, { useState, useEffect, useRef } from 'react';
import './Settings.css';
import { Music, Volume2, Globe, ChevronDown, Check, ChevronRight } from 'lucide-react';
import { useAudio } from '../utils/AudioContext';
import { useTranslation } from 'react-i18next';
import { 
    playUiHoverSound, 
    playUiClickSound, 
    playUiOpenSound, 
    playUiCloseSound 
} from '../utils/sounds'; // Import sounds

const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'pt', name: 'Português' },
    { code: 'fr', name: 'Français' },
    { code: 'zh', name: '中文' },
];

function Settings({ onClose }) {
    const { musicMuted, setMusicMuted, sfxMuted, setSfxMuted } = useAudio();
    const { t, i18n } = useTranslation();
    const [isExiting, setIsExiting] = useState(false);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [selectedLang, setSelectedLang] = useState(languages.find(l => l.code === i18n.language)?.name || 'English');
    const dropdownRef = useRef(null);

    // Play Open Sound on Mount
    useEffect(() => {
        playUiOpenSound(sfxMuted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = () => {
        playUiCloseSound(sfxMuted);
        setIsExiting(true);
    };

    const handleAnimationEnd = (e) => {
        if (isExiting && (e.animationName.includes('popOut') || e.animationName.includes('fadeOut'))) {
            onClose();
        }
    };

    const toggleDropdown = () => {
        playUiClickSound(sfxMuted);
        setDropdownOpen(!isDropdownOpen);
    };

    const selectLang = (lang) => {
        playUiClickSound(sfxMuted);
        setSelectedLang(lang.name);
        i18n.changeLanguage(lang.code);
        setDropdownOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    // Listen for Escape key to close settings
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isExiting) return;
                handleClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isExiting]);

    return (
        <div className={`settings-container ${isExiting ? 'fade-out' : ''}`} onAnimationEnd={handleAnimationEnd}>
            <div className={`settings-modal ${isExiting ? 'pop-out' : ''}`}>
                <h1 className="modal-title">
                    {t('settings').split('').map((char, index) => {
                        const colors = ['c-g', 'c-b', 'c-r', 'c-y', 'c-g', 'c-b', 'c-p', 'c-r'];
                        const color = colors[index % colors.length];
                        return <span key={index} className={color}>{char}</span>;
                    })}
                </h1>

                <div className="controls-container">
                    {/* Music */}
                    <div className="control-row">
                        <div className="control-label">
                            <Music /> {t('music')}
                        </div>
                        <label className="switch">
                            <input 
                                type="checkbox" 
                                checked={!musicMuted} 
                                onChange={() => { playUiClickSound(sfxMuted); setMusicMuted(!musicMuted); }} 
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    {/* SFX */}
                    <div className="control-row">
                        <div className="control-label">
                            <Volume2 /> {t('sfx')}
                        </div>
                        <label className="switch">
                            <input 
                                type="checkbox" 
                                checked={!sfxMuted} 
                                onChange={() => { playUiClickSound(sfxMuted); setSfxMuted(!sfxMuted); }} 
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    {/* Language Dropdown */}
                    <div className="control-row">
                        <div className="control-label">
                            <Globe /> {t('language')}
                        </div>
                        
                        <div className={`custom-dropdown ${isDropdownOpen ? 'open' : ''}`} ref={dropdownRef}>
                            <div className="dropdown-trigger" onClick={toggleDropdown} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                <span>{selectedLang}</span>
                                <ChevronDown />
                            </div>

                            <div className="dropdown-menu">
                                {languages.map(lang => (
                                    <div 
                                        key={lang.code}
                                        className={`dropdown-item ${selectedLang === lang.name ? 'selected' : ''}`}
                                        onClick={() => selectLang(lang)}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        {lang.name} <Check className="check-icon" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="links-stack">
                    <button className="btn-link" onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                        {t('privacy_policy')} <ChevronRight />
                    </button>
                    <button className="btn-link" onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                        {t('terms_and_conditions')} <ChevronRight />
                    </button>
                    <button className="btn-link" onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                        {t('credits')} <ChevronRight />
                    </button>
                    <button className="btn-link" onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                        {t('support_contact')} <ChevronRight />
                    </button>
                </div>

                <button className="btn-done" onClick={handleClose} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                    <Check /> {t('done')}
                </button>

                <div className="version-text">{t('version')} 1.0.0</div>
            </div>
        </div>
    );
}

export default Settings;